import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  CardActionArea,
  Chip,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Pagination,
  Button,
  Stack,
  Divider,
  IconButton,
  Paper,
  Avatar,
} from '@mui/material';
import {
  Search,
  Sort,
  ThumbUp,
  Download,
  FilterList,
  Favorite,
  Clear,
  Person,
  AutoFixHigh
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import ApiFetch from '../ApiFetch';
import FavoriteIconV2 from '../FavoriteIconV2';

const ModelListPage = () => {
  const [models, setModels] = useState([]);
  const [filteredModels, setFilteredModels] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortOption, setSortOption] = useState('newest');
  const [filterOptions, setFilterOptions] = useState({
    category: 'all',
    showFavorites: false,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12;
  const [showFilters, setShowFilters] = useState(false);

  // カテゴリー一覧
  const categories = [
    { value: 'all', label: 'すべて' },
    { value: 'realistic', label: 'リアルスタイル' },
    { value: 'anime', label: 'アニメスタイル' },
    { value: 'illustration', label: 'イラストスタイル' },
    { value: 'character', label: 'キャラクターモデル' },
    { value: 'landscape', label: '風景' },
  ];

  // 並び替えオプション
  const sortOptions = [
    { value: 'newest', label: '新着順' },
    { value: 'popular', label: '人気順' },
    { value: 'downloads', label: 'ダウンロード数' },
    { value: 'usedCount', label: '使用数' },
  ];

  // モデル一覧を取得する関数
  const fetchModels = async () => {
    setIsLoading(true);
    try {
      const response = await new ApiFetch().get(
        `${ApiFetch.API_PROTOCOL}//${ApiFetch.API_HOST}/v1/model/list`
      );
      const json = await response.json();
      
      if (json.result) {
        setModels(json.data);
        setFilteredModels(json.data);
      }
    } catch (error) {
      console.error('Error fetching model list:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // 初回ロード時にモデル一覧を取得
  useEffect(() => {
    fetchModels();
  }, []);

  // フィルタリングと並び替え適用
  useEffect(() => {
    let result = [...models];

    // 検索フィルター適用
    if (searchTerm) {
      result = result.filter(model => 
        model.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        model.user_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (model.tags && model.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase())))
      );
    }

    // カテゴリーフィルター適用
    if (filterOptions.category !== 'all') {
      result = result.filter(model => model.category === filterOptions.category);
    }

    // お気に入りフィルター適用
    if (filterOptions.showFavorites) {
      result = result.filter(model => model.favorite > 0);
    }

    // 並び替え適用
    switch (sortOption) {
      case 'popular':
        result.sort((a, b) => b.favorite_count - a.favorite_count);
        break;
      case 'downloads':
        result.sort((a, b) => b.download_count - a.download_count);
        break;
      case 'usedCount':
        result.sort((a, b) => b.used_count - a.used_count);
        break;
      case 'newest':
      default:
        result.sort((a, b) => new Date(b.updated) - new Date(a.updated));
        break;
    }

    setFilteredModels(result);
    setCurrentPage(1); // フィルター変更時はページを1に戻す
  }, [searchTerm, filterOptions, sortOption, models]);

  // ページネーション用のデータ
  const totalPages = Math.ceil(filteredModels.length / itemsPerPage);
  const currentItems = filteredModels.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // フィルターリセット
  const resetFilters = () => {
    setSearchTerm('');
    setSortOption('newest');
    setFilterOptions({
      category: 'all',
      showFavorites: false,
    });
  };

  // モックデータ（API実装前用）
  const mockModels = [
    {
      id: 'model1',
      name: 'リアルスタイル風景生成モデル',
      user_id: 'user1',
      user_name: 'landscape_creator',
      thumbnail: '/api/placeholder/400/400',
      favorite: 1,
      favorite_count: 3245,
      download_count: 12500,
      used_count: 45200,
      category: 'realistic',
      updated: '2024-01-20',
      description: '高品質な風景画像を生成する最新モデル',
      tags: ['風景', 'リアル', '4K', 'HDR'],
      version: 'v2.0'
    },
    {
      id: 'model2',
      name: 'アニメキャラクターモデル',
      user_id: 'user2',
      user_name: 'anime_artist',
      thumbnail: '/api/placeholder/400/400',
      favorite: 0,
      favorite_count: 2856,
      download_count: 9800,
      used_count: 32100,
      category: 'anime',
      updated: '2024-01-15',
      description: 'アニメスタイルのキャラクターを簡単に生成',
      tags: ['アニメ', 'キャラクター', '2D'],
      version: 'v1.5'
    },
    {
      id: 'model3',
      name: 'イラストレーションスタイル',
      user_id: 'user3',
      user_name: 'illustration_pro',
      thumbnail: '/api/placeholder/400/400',
      favorite: 1,
      favorite_count: 1987,
      download_count: 7650,
      used_count: 25400,
      category: 'illustration',
      updated: '2024-01-10',
      description: 'プロイラストレーター監修のスタイルモデル',
      tags: ['イラスト', 'デジタルアート', 'プロフェッショナル'],
      version: 'v1.0'
    },
    {
      id: 'model4',
      name: 'ファンタジー風景生成器',
      user_id: 'user4',
      user_name: 'fantasy_worlds',
      thumbnail: '/api/placeholder/400/400',
      favorite: 0,
      favorite_count: 1543,
      download_count: 5800,
      used_count: 18900,
      category: 'landscape',
      updated: '2024-01-05',
      description: 'ファンタジー世界の風景を生成するAIモデル',
      tags: ['ファンタジー', '風景', '魔法', '異世界'],
      version: 'v1.2'
    },
    {
      id: 'model5',
      name: 'リアルポートレートジェネレーター',
      user_id: 'user5',
      user_name: 'portrait_master',
      thumbnail: '/api/placeholder/400/400',
      favorite: 1,
      favorite_count: 2123,
      download_count: 8900,
      used_count: 29700,
      category: 'realistic',
      updated: '2023-12-28',
      description: 'リアルな人物ポートレート生成に特化',
      tags: ['ポートレート', 'リアル', '人物', '写真調'],
      version: 'v2.1'
    },
    {
      id: 'model6',
      name: 'アニメシーンジェネレーター',
      user_id: 'user6',
      user_name: 'anime_scene',
      thumbnail: '/api/placeholder/400/400',
      favorite: 0,
      favorite_count: 1876,
      download_count: 6300,
      used_count: 21500,
      category: 'anime',
      updated: '2023-12-20',
      description: 'アニメシーンの背景と人物を一括生成',
      tags: ['アニメ', 'シーン', '背景', 'キャラクター'],
      version: 'v1.3'
    },
  ];

  // APIが未実装の場合はモックデータを使用
  useEffect(() => {
    if (models.length === 0 && !isLoading) {
      setModels(mockModels);
      setFilteredModels(mockModels);
    }
  }, [isLoading, models.length]);

  return (
    <Box sx={{ bgcolor: 'grey.50', minHeight: '100vh', py: 4 }}>
      <Box sx={{ maxWidth: 'xl', mx: 'auto', px: 4 }}>
        {/* ヘッダーセクション */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            画像生成モデル一覧
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            各種スタイルに特化した画像生成モデルの一覧です。モデルを選択して、あなたのアイデアを形にしましょう。
          </Typography>
        </Box>

        {/* 検索・フィルターセクション */}
        <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                variant="outlined"
                placeholder="モデルを検索"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                  endAdornment: searchTerm && (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setSearchTerm('')}>
                        <Clear fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth variant="outlined">
                <InputLabel>並び替え</InputLabel>
                <Select
                  value={sortOption}
                  onChange={(e) => setSortOption(e.target.value)}
                  label="並び替え"
                  startAdornment={
                    <InputAdornment position="start">
                      <Sort fontSize="small" />
                    </InputAdornment>
                  }
                >
                  {sortOptions.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<FilterList />}
                onClick={() => setShowFilters(!showFilters)}
                sx={{ height: '56px' }}
              >
                フィルターオプション {showFilters ? '▲' : '▼'}
              </Button>
            </Grid>
            
            {showFilters && (
              <>
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel>カテゴリー</InputLabel>
                    <Select
                      value={filterOptions.category}
                      onChange={(e) => setFilterOptions({...filterOptions, category: e.target.value})}
                      label="カテゴリー"
                    >
                      {categories.map(cat => (
                        <MenuItem key={cat.value} value={cat.value}>
                          {cat.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <Button
                    variant={filterOptions.showFavorites ? "contained" : "outlined"}
                    startIcon={<Favorite />}
                    onClick={() => setFilterOptions({...filterOptions, showFavorites: !filterOptions.showFavorites})}
                    sx={{ height: '56px', width: '100%' }}
                  >
                    お気に入りのみ表示
                  </Button>
                </Grid>
                <Grid item xs={12} sm={12} md={4}>
                  <Button
                    variant="text"
                    onClick={resetFilters}
                    sx={{ height: '56px', width: '100%' }}
                  >
                    フィルターをリセット
                  </Button>
                </Grid>
              </>
            )}
          </Grid>
        </Paper>

        {/* 検索結果情報 */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {filteredModels.length} 件のモデルが見つかりました
          </Typography>
          <Chip 
            label={`${sortOptions.find(opt => opt.value === sortOption)?.label || '新着順'}`}
            size="small"
            onDelete={() => setSortOption('newest')}
            deleteIcon={<Clear fontSize="small" />}
            sx={{ display: sortOption === 'newest' ? 'none' : 'flex' }}
          />
        </Box>

        {/* モデル一覧 */}
        {isLoading ? (
          <Typography>読み込み中...</Typography>
        ) : currentItems.length > 0 ? (
          <Grid container spacing={3}>
            {currentItems.map((model) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={model.id}>
                <Card sx={{ 
                  height: '100%', 
                  display: 'flex', 
                  flexDirection: 'column',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 6
                  }
                }}>
                  <CardActionArea component={Link} to={`/model/${model.id}`}>
                    <CardMedia
                      component="img"
                      height="200"
                      image={model.thumbnail}
                      alt={model.name}
                    />
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Typography variant="h6" component="div" noWrap>
                        {model.name}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 1, mb: 2 }}>
                        <Avatar sx={{ width: 24, height: 24, mr: 1, bgcolor: 'primary.main' }}>
                          <Person fontSize="small" />
                        </Avatar>
                        <Typography variant="body2" color="text.secondary">
                          @{model.user_name}
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ 
                        mb: 2,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        height: '40px'
                      }}>
                        {model.description}
                      </Typography>
                      
                      <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: '4px' }}>
                        <Chip size="small" label={model.category} />
                        <Chip size="small" label={model.version} variant="outlined" />
                      </Stack>
                      
                      <Divider sx={{ mb: 2 }} />
                      
                      <Grid container>
                        <Grid item xs={4} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <FavoriteIconV2 
                            style={{cursor:"pointer", fontSize:"16px"}} 
                            type="model" 
                            id={model.id} 
                            favorite={model.favorite} 
                            dialog={() => {}} 
                            count={model.favorite_count} 
                          />
                        </Grid>
                        <Grid item xs={4} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <Download fontSize="small" sx={{ mr: 0.5 }} />
                          <Typography variant="body2">
                            {formatNumber(model.download_count)}
                          </Typography>
                        </Grid>
                        <Grid item xs={4} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <AutoFixHigh fontSize="small" sx={{ mr: 0.5 }} />
                          <Typography variant="body2">
                            {formatNumber(model.used_count)}
                          </Typography>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant="h6" gutterBottom>
              検索条件に一致するモデルが見つかりませんでした
            </Typography>
            <Typography variant="body2" color="text.secondary">
              検索条件を変更するか、フィルターをリセットしてください
            </Typography>
            <Button 
              variant="contained" 
              sx={{ mt: 2 }}
              onClick={resetFilters}
            >
              フィルターをリセット
            </Button>
          </Box>
        )}

        {/* ページネーション */}
        {filteredModels.length > itemsPerPage && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={(event, value) => setCurrentPage(value)}
              color="primary"
              size="large"
              showFirstButton
              showLastButton
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

// 数値フォーマット関数（1000→1k など）
const formatNumber = (num) => {
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1) + 'M';
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(1) + 'k';
  }
  return num;
};

export default ModelListPage;